/**
 * The country orbs. SPEC.md §19, §17.1, §47.
 *
 * A sheet on the landing screen rather than a screen of its own, because it is
 * one question with four answers and the app already has exactly one modal
 * surface. §47
 *
 * **It imports no catalogue.** Everything here comes from `i18n/locales.ts` and
 * `i18n/Flag.tsx`, which between them are four names, four flags and thirty
 * lines of geometry — so putting a Tutorial button on the landing screen costs
 * the landing bundle nothing that matters. The chapters, the engine and the
 * sixty kilobytes of translated copy arrive only once somebody picks a language.
 * §17.1
 *
 * Each orb is a flag *and* the language's own name, and the name is the part
 * that is load-bearing: a flag is a country rather than a language, several
 * languages here are spoken well outside the country drawn, and a learner who
 * cannot read the app cannot be expected to infer "Thai" from a tricolour. The
 * flag is what makes the right orb findable in one glance; the endonym is what
 * makes it correct. §19
 */

import { Sheet } from '@/components/ui/Sheet';
import { Flag } from './i18n/Flag';
import { LOCALES, type LocaleCode } from './i18n/locales';

export interface LanguagePickerProps {
  open: boolean;
  onClose(): void;
  onPick(code: LocaleCode): void;
}

export function LanguagePicker({ open, onClose, onPick }: LanguagePickerProps) {
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Tutorial"
      description="Learn Minimizer in about ten minutes. Pick a language."
    >
      <ul className="m-0 grid list-none grid-cols-2 gap-3 p-0 pb-2 sm:grid-cols-4">
        {LOCALES.map((locale) => (
          <li key={locale.code}>
            <button
              type="button"
              lang={locale.code}
              onClick={() => {
                onPick(locale.code);
              }}
              /*
                The accessible name is the English name, not the endonym: this
                button sits on the landing screen, which is an English screen, so
                a screen reader set to English announces "Vietnamese" rather than
                attempting "Tiếng Việt" in an English voice. The visible label
                stays the endonym, and `lang` on the button is what lets a reader
                that *can* switch voices do so. §19
              */
              aria-label={locale.englishName}
              className={[
                'flex w-full flex-col items-center gap-2 rounded-card p-2',
                'transition-[transform,background-color] duration-150 ease-out-soft',
                'active:scale-[0.96] hover:bg-raised',
              ].join(' ')}
            >
              {/*
                The orb. A circle with the flag sliced to fill it, a soft rim so
                it reads as a physical token rather than a cropped picture, and a
                highlight across the top-left that gives it the roundness the
                word "orb" is promising.
              */}
              <span
                aria-hidden="true"
                className="relative block size-16 overflow-hidden rounded-full shadow-card ring-1 ring-line sm:size-[4.5rem]"
              >
                <Flag code={locale.flag} className="absolute inset-0 size-full" />
                <span className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_30%_25%,rgba(255,255,255,0.38),transparent_58%)]" />
                <span className="absolute inset-0 rounded-full shadow-[inset_0_-6px_12px_rgba(0,0,0,0.28)]" />
              </span>
              <span className="text-center text-sm leading-tight font-semibold text-ink">
                {locale.endonym}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Sheet>
  );
}
